import React, { useState, useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
import './ScoreSubmitBar.scss';
import TwoButtonModal from "../../GamePageComponents/Modals/TwoButtonModal";
import ResponseModal from "../../GameModals/ResponseModal/ResponseModal";
import { useSaveGolfLeagueScores } from "../../../hooks/pools/useSaveGolfLeagueScores";

const ScoreSubmitBar = ({ 
    invalidEntries,
    masterPoolId,
    completedTeamsCount,
    teamsCount,
    round,
    eventId,
    playerState,
    setPlayerState,
    players,
    selectedEvent,
    teams
}) => {

    const [modalOpen, setModalOpen] = useState(false);
    const [responseModalOpen, setResponseModalOpen] = useState(false);
    const [responseStatus, setResponseStatus] = useState('');
    const [responseMessage, setResponseMessage] = useState('');

    const { mutate: saveScores } = useSaveGolfLeagueScores();

    const handleSubmit = async () => {
        const data = {
            masterPoolId: masterPoolId,
            playerScores: playerState,
            round: Number(round),
            eventId: eventId,
            par: selectedEvent?.par,
            totalRounds: selectedEvent?.rounds,
            allPlayersScoresIn: playersWithScoreForRound?.length === howManyPlayers,
            teams: teams,
        }
        //console.log("Data to submit", data);

        try {
            await saveScores(data, {
                onSuccess: (response) => {
                    //console.log("Scores saved successfully", response);
                    // Set success message with player and team counts
                    setResponseStatus('success');
                    setResponseMessage(
                        `Successfully submitted scores!\n\nTeams: ${completedTeamsCount}/${teamsCount}\nPlayers: ${playersWithScoreForRound?.length}/${howManyPlayers}`
                    );
                    setResponseModalOpen(true);
                },
                onError: (error) => {
                    console.error("Error saving scores", error);
                    // Set error message
                    setResponseStatus('error');
                    setResponseMessage(error.response.data.message || "An error occurred while saving scores. Please try again.");
                    setResponseModalOpen(true);
                }
            });
        } catch (error) {
            console.error("Error submitting scores", error);
            // Set error message for caught exceptions
            setResponseStatus('error');
            setResponseMessage(error.message || "An unexpected error occurred. Please try again.");
            setResponseModalOpen(true);
        }
    };

    const handleCancel = () => {
        //console.log("Cancel clicked");
        setModalOpen(true);
    };

    const playersWithScoreForRound = useMemo(() => {
        return playerState?.filter(player => {
            const playerScores = player?.scores?.[eventId]?.[`round${round}`];
            return playerScores !== undefined && playerScores !== null;
        });
    }, [playerState, eventId, round]);
    //console.log("Players With Score For Round", playersWithScoreForRound, playersWithScoreForRound?.length);

    const howManyPlayers = useMemo(() => {
        return players?.length || 0;
    }, [players]);

    const headerText = "Are you sure you want to clear all of your scores?";
    const contentText = "This cannot be undone.";
    const buttonOneText = "CANCEL";
    const buttonTwoText = "CLEAR SCORES";


    return (
        <Box className="scores-submit-bar-container">
            {/* Box for text info */}
            <Box className="scores-submit-bar-content-row">
                <Box className="scores-submit-bar-text-group">
                    {/* Teams */}
                    <Typography className="scores-submit-bar-typography">
                        <Box component="span" sx={{ color: completedTeamsCount === teamsCount ? '#00AA72' : '#CC293C' }}>
                            {completedTeamsCount}/{teamsCount}
                        </Box>
                        <Box component="span" sx={{ color: '#002129', marginLeft: '4px' }}>
                            Teams
                        </Box>
                    </Typography>

                    {/* Players */}
                    <Typography className="scores-submit-bar-typography" sx={{ marginLeft: '24px' }}>
                        <Box component="span" sx={{ color: playersWithScoreForRound?.length === howManyPlayers ? '#00AA72' : '#CC293C' }}>
                            {playersWithScoreForRound?.length}/{howManyPlayers}
                        </Box>
                        <Box component="span" sx={{ color: '#002129', marginLeft: '4px' }}>
                            Players
                        </Box>
                    </Typography>
                </Box>
            </Box>
    
            {/* Cancel and Submit buttons */}
            <Box className="scores-submit-bar-button-group">
                <Button 
                    variant="contained" 
                    className="scores-submit-bar-cancel-button" 
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSubmit}
                    disabled={invalidEntries?.length > 0}
                >
                    Submit
                </Button>
            </Box>

            {/* Cancel Confirmation Modal */}
            <TwoButtonModal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                headerText={headerText}
                contentText={contentText}
                buttonOneText={buttonOneText}
                buttonTwoText={buttonTwoText}
                buttonOneAction={() => {
                    setModalOpen(false);
                }}
                buttonTwoAction={() => {
                    setModalOpen(false);
                    setPlayerState(players);
                }}
            />

            {/* Response Modal for success/error */}
            <ResponseModal
                open={responseModalOpen}
                onClose={() => setResponseModalOpen(false)}
                status={responseStatus}
                message={responseMessage}
            />
        </Box>
    );
};

export default ScoreSubmitBar;